/**
 * MobileVcompTopMenu
 *
 * Mobile-optimized top navigation for VcompView.
 * Replaces the desktop topMenu div when isMobile === 2.
 * Uses MUI Box, IconButton, Tabs, and Typography for a compact AppBar-style header.
 */
import React from "react";
import { Box, IconButton, Typography, Fade, Tabs, Tab } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
    transform: "scale(1.06)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

/**
 * @param {Object} props
 * @param {number}   props.selectedView     - 0 (Participate) or 1 (Manage)
 * @param {Array}    props.viewsOptions     - [{label, value}]
 * @param {Function} props.onViewChange     - (value) => void
 * @param {boolean}  props.showConfigButton - show config icon
 * @param {boolean}  props.showAddButton    - show add icon
 * @param {Function} props.onConfigClick    - () => void
 * @param {Function} props.onAddClick       - () => void
 */
export default function MobileVcompTopMenu({
  selectedView,
  viewsOptions,
  onViewChange,
  showConfigButton,
  showAddButton,
  onConfigClick,
  onAddClick,
}) {
  const handleTabChange = (_event, newValue) => {
    onViewChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 4px 6px -4px rgba(0,40,128,0.05), 0 2px 3px -2px rgba(0,0,0,0.03)",
        px: 1.5,
        py: 0.75,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        zIndex: 10,
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Row 1: Title + action icons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        {/* Left: title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "rgb(0,0,0)",
              letterSpacing: "-0.025em",
              lineHeight: 1.2,
              transition: "color 0.2s ease",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Langs str="V-Comp" />
          </Typography>
        </Box>

        {/* Right: config + add buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {showConfigButton && (
            <Fade in timeout={200}>
              <IconButton
                onClick={onConfigClick}
                size="large"
                aria-label="Configuration"
                sx={iconButtonSx}
              >
                <SettingsOutlinedIcon />
              </IconButton>
            </Fade>
          )}
          {showAddButton && (
            <Fade in timeout={250}>
              <IconButton
                onClick={onAddClick}
                size="large"
                aria-label="Ajouter un projet"
                sx={iconButtonSx}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Fade>
          )}
        </Box>
      </Box>

      {/* Row 2: View tabs */}
      {viewsOptions.length > 0 && (
        <Box sx={{ px: 0.5, pb: 0.5 }}>
          <Tabs
            value={selectedView}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              "& .MuiTabs-indicator": {
                backgroundColor: "rgb(255,144,21)",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "rgba(0,0,0,0.5)",
                transition: TRANSITION_SMOOTH,
                "&.Mui-selected": {
                  color: "rgb(255,144,21)",
                },
              },
            }}
          >
            {viewsOptions.map((opt) => (
              <Tab key={opt.value} label={opt.label} value={opt.value} />
            ))}
          </Tabs>
        </Box>
      )}
    </Box>
  );
}
